import { Component } from 'react';

/**
 * Captura errores de tipo "Failed to fetch dynamically imported module".
 *
 * Estos errores son típicos cuando el service worker tiene cacheado un
 * `index.html` viejo que referencia chunks con hash que ya no existen
 * en producción (porque se hizo un deploy nuevo con hashes nuevos). El
 * usuario aterriza en la página con un index stale, intenta cargar
 * TradeMatch / otro componente lazy, y vite-plugin-pwa devuelve 404.
 *
 * Estrategia:
 *  - Detectamos el error vía componentDidCatch.
 *  - Si es chunk-load, intentamos UNA recarga limpia (location.reload
 *    fuerza al SW a re-fetchear). El flag en sessionStorage evita
 *    bucles infinitos si la recarga no soluciona.
 *  - Si no es un chunk error, propagamos al error boundary general.
 *
 * Es defensivo. La causa raíz (friendUid === user.uid disparando
 * TradeMatch erróneamente) se arregló en App.jsx, pero otro chunk
 * podría fallar por la misma razón en el futuro.
 */
const RELOAD_FLAG = 'chunkErrorBoundary_reloaded';
const STALE_RELOAD_FLAG = 'chunkErrorBoundary_staleReloaded';

function isChunkLoadError(error) {
  if (!error) return false;
  const msg = String(error.message || error.toString());
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes("error loading dynamically imported module")
  );
}

/**
 * Heurística: errores como ReferenceError, TypeError de prop indefinida,
 * etc., suelen indicar que el bundle cacheado del SW es viejo y el código
 * actual ya no tiene el bug. Auto-curar limpiando caches del SW + reload.
 *
 * NOTA: solo intentamos una vez por sesión (STALE_RELOAD_FLAG). Si el
 * error persiste después del reload, mostramos el fallback normal — eso
 * indica que el bug está en el código actual, no en cache vieja.
 */
function looksLikeStaleBundle(error) {
  if (!error) return false;
  // ReferenceError clásico de scope rota por refactor (ej.
  // sortedFriendsList undefined post-deploy con SW viejo).
  if (error.name === 'ReferenceError') return true;
  const msg = String(error.message || '');
  // TypeError sobre propiedad inexistente — también típico de
  // refactors donde el shape cambió y el cache trae el viejo.
  if (/Cannot read propert(y|ies) of (undefined|null)/.test(msg)) return true;
  return false;
}

async function clearCachesAndUnregisterSW() {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
  } catch (e) { console.warn('cache clear failed', e); }
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    }
  } catch (e) { console.warn('SW unregister failed', e); }
}

export default class ChunkErrorBoundary extends Component {
  state = { hasError: false, isChunkError: false, isStaleBundle: false };

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
      isStaleBundle: !isChunkLoadError(error) && looksLikeStaleBundle(error),
    };
  }

  componentDidCatch(error, info) {
    console.error('ChunkErrorBoundary caught:', error, info);

    if (isChunkLoadError(error)) {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1';
      if (!alreadyReloaded) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        setTimeout(() => window.location.reload(), 900);
      }
      return;
    }

    // Stale bundle: el código que se está ejecutando es del SW cache
    // viejo. Limpiar caches + unregister SW + reload normalmente trae
    // el bundle nuevo y arregla el bug sin intervención del usuario.
    if (looksLikeStaleBundle(error)) {
      const alreadyTried = sessionStorage.getItem(STALE_RELOAD_FLAG) === '1';
      if (!alreadyTried) {
        sessionStorage.setItem(STALE_RELOAD_FLAG, '1');
        clearCachesAndUnregisterSW().finally(() => {
          setTimeout(() => window.location.reload(), 700);
        });
      }
    }
  }

  componentDidUpdate(_, prevState) {
    // Cuando ya estamos en un estado "sin error" y veníamos de uno,
    // limpiar los flags para que un futuro error pueda volver a
    // recargar (la recarga anterior ya curó la sesión).
    if (prevState.hasError && !this.state.hasError) {
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
        sessionStorage.removeItem(STALE_RELOAD_FLAG);
      } catch { /* noop */ }
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.isChunkError || this.state.isStaleBundle) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          minHeight: '60vh', padding: '24px',
          textAlign: 'center', color: 'var(--text-primary)',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔄</div>
          <h2 style={{ marginBottom: 8, color: 'var(--accent-gold)' }}>
            Actualizando la app…
          </h2>
          <p style={{ color: 'var(--text-muted)', maxWidth: 440 }}>
            Detectamos una versión más reciente y estamos recargando para que
            tengas los últimos cambios. Si esto no se cierra solo en unos
            segundos, recarga la página manualmente (Ctrl/Cmd + Shift + R).
          </p>
        </div>
      );
    }

    // Error genérico (no chunk). Mostramos un fallback simple con
    // botón de recuperación manual.
    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', padding: '24px',
        textAlign: 'center', color: 'var(--text-primary)',
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>⚠️</div>
        <h2 style={{ marginBottom: 8 }}>Algo salió mal</h2>
        <p style={{ color: 'var(--text-muted)', maxWidth: 440, marginBottom: 20 }}>
          La app encontró un error inesperado. Recarga la página o cierra
          sesión y vuelve a entrar.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            className="btn btn-gold"
            onClick={() => window.location.reload()}
            style={{ padding: '10px 24px', fontSize: '0.9rem' }}
          >
            Recargar app
          </button>
          <button
            className="btn"
            onClick={async () => {
              await clearCachesAndUnregisterSW();
              window.location.reload();
            }}
            style={{
              padding: '10px 24px',
              fontSize: '0.9rem',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-secondary)',
            }}
            title="Limpia el cache del service worker y recarga"
          >
            🧹 Forzar actualización
          </button>
        </div>
      </div>
    );
  }
}
